import { useState, type ReactNode } from 'react'
import { ArrowLeft, Building2, Loader2, Pencil, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import {
  useGetAssociatedParty,
  useDeleteAssociatedParty,
  type AssociatedPartyProductAssignment,
} from '@/shared/hooks/api/associatedParties'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { ThemeSwitch } from '@/components/theme-switch'
import { NotFoundView } from '@/features/errors/not-found-view'
import { buildMachineryDirectiveStudioBaseHref } from '@/features/compliance-studio/machinery-directive/machineryDirectiveStudioPaths'
import { associatedPartiesCopyKey } from './associatedPartyCopy'
import {
  getAssociatedPartiesListPath,
  isV2AssociatedPartiesShell,
} from './associatedPartyPaths'
import { EditAssociatedPartyModal } from './EditAssociatedPartyModal'
import { formatAssociatedPartyAddress } from './formatAddress'

function buildProductAssignmentPath(
  assignment: AssociatedPartyProductAssignment
): string {
  if (assignment.machineryDirectiveComplianceId) {
    return buildMachineryDirectiveStudioBaseHref(
      assignment.productId,
      assignment.machineryDirectiveComplianceId
    )
  }
  return `/product-catalog/${assignment.productId}`
}

function AssociatedPartiesClassicChrome({
  children,
}: {
  children: ReactNode
}) {
  return (
    <>
      <Header fixed>
        <div className='ms-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>
      <Main className='flex flex-1 flex-col gap-4 sm:gap-6'>{children}</Main>
    </>
  )
}

function AssociatedPartyDetailsShell({
  embedInV2Shell,
  children,
}: {
  embedInV2Shell: boolean
  children: ReactNode
}) {
  if (embedInV2Shell) {
    return <div className='flex flex-col gap-4 sm:gap-6'>{children}</div>
  }
  return <AssociatedPartiesClassicChrome>{children}</AssociatedPartiesClassicChrome>
}

export function AssociatedPartyDetailsPage() {
  const { associatedPartyId } = useParams<{ associatedPartyId: string }>()
  const { pathname } = useLocation()
  const embedInV2Shell = isV2AssociatedPartiesShell(pathname)
  const listPath = getAssociatedPartiesListPath(pathname)
  const navigate = useNavigate()
  const { t } = useTranslation([
    'associatedParties',
    'countries',
    'common',
    'machineryDirectiveCompliance',
  ])
  const copy = (key: string) => t(associatedPartiesCopyKey(pathname, key))
  const backToListLabel = embedInV2Shell
    ? t('common:v2.pages.organisationsAndRoles.backToList')
    : t('details.backToList')
  const {
    data: party,
    isLoading,
    isError,
  } = useGetAssociatedParty(associatedPartyId)
  const { mutateAsync: deleteAssociatedParty, isPending: isDeleting } =
    useDeleteAssociatedParty(associatedPartyId ?? '')
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  if (isLoading) {
    return (
      <AssociatedPartyDetailsShell embedInV2Shell={embedInV2Shell}>
        <div className='flex items-center justify-center py-12 text-muted-foreground'>
          {t('loading')}
        </div>
      </AssociatedPartyDetailsShell>
    )
  }

  if (isError || !party) {
    return (
      <AssociatedPartyDetailsShell embedInV2Shell={embedInV2Shell}>
        <NotFoundView
          showCode={false}
          title={copy('details.notFound.title')}
          description={copy('details.notFound.description')}
          primaryAction={{
            label: backToListLabel,
            to: listPath,
          }}
        />
      </AssociatedPartyDetailsShell>
    )
  }

  const contactDetails = [
    party.contactName?.trim(),
    party.contactPhoneNumber?.trim(),
    party.contactEmail?.trim(),
  ].filter((value): value is string => Boolean(value))
  const hasCompletedProductAssignments = party.productAssignments.some(
    (assignment) => assignment.completed
  )

  const handleDelete = async () => {
    try {
      await deleteAssociatedParty()
      toast.success(copy('details.delete.success'))
      setIsDeleteDialogOpen(false)
      navigate(listPath)
    } catch (error) {
      const message =
        error instanceof Error ? error.message : copy('details.delete.error')
      toast.error(message)
    }
  }

  return (
    <>
      <AssociatedPartyDetailsShell embedInV2Shell={embedInV2Shell}>
        <div>
          <div>
            <Button
              variant='ghost'
              className='mb-2 px-0'
              onClick={() => navigate(listPath)}
            >
              <ArrowLeft className='size-4' />
              {backToListLabel}
            </Button>
          </div>
          <div className='flex flex-wrap items-start justify-between gap-4'>
            <div className='min-w-0 flex-1'>
              <h2 className='text-2xl font-bold tracking-tight'>
                {copy('details.title')}
              </h2>
              <p className='text-muted-foreground'>{party.name}</p>
            </div>
            <div className='flex shrink-0 flex-wrap gap-2'>
              <Button
                type='button'
                className='gap-2'
                onClick={() => setIsEditModalOpen(true)}
              >
                <Pencil className='size-4' aria-hidden />
                {t('common:actions.edit')}
              </Button>
              {hasCompletedProductAssignments ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className='inline-flex'>
                      <Button
                        type='button'
                        variant='destructive'
                        className='gap-2'
                        disabled
                      >
                        <Trash2 className='size-4' aria-hidden />
                        {t('common:actions.delete')}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {copy('details.delete.disabledTooltip')}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  type='button'
                  variant='destructive'
                  className='gap-2'
                  onClick={() => setIsDeleteDialogOpen(true)}
                >
                  <Trash2 className='size-4' aria-hidden />
                  {t('common:actions.delete')}
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className='grid gap-4 lg:grid-cols-3'>
          <Card>
            <CardHeader>
              <CardTitle>{t('details.cards.name')}</CardTitle>
            </CardHeader>
            <CardContent>{party.name}</CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>{t('details.cards.address')}</CardTitle>
            </CardHeader>
            <CardContent>
              {formatAssociatedPartyAddress(party, {
                getCountryName: (code) => t(code, { ns: 'countries' }) || code,
                translate: (key, options) => t(key, options),
              })}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>{t('details.cards.contactPerson')}</CardTitle>
            </CardHeader>
            <CardContent>
              {contactDetails.length > 0
                ? contactDetails.join(' · ')
                : t('details.noContactDetails')}
            </CardContent>
          </Card>
        </div>

        <section className='space-y-3'>
          <h3 className='text-lg font-semibold tracking-tight'>
            {t('details.productAssignments.title')}
          </h3>
          {party.productAssignments.length === 0 ? (
            <div className='rounded-md border border-dashed'>
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant='icon'>
                    <Building2 className='size-5' />
                  </EmptyMedia>
                  <EmptyTitle>
                    {t('details.productAssignments.empty.title')}
                  </EmptyTitle>
                  <EmptyDescription>
                    {copy('details.productAssignments.empty.description')}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
          ) : (
            <div className='overflow-x-auto rounded-md border'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t('details.productAssignments.columns.product')}
                    </TableHead>
                    <TableHead>
                      {t('details.productAssignments.columns.role')}
                    </TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {party.productAssignments.map((assignment) => (
                    <TableRow
                      key={`${assignment.productId}-${assignment.machineryDirectiveComplianceId}-${assignment.role}`}
                      className='cursor-pointer'
                      onClick={() =>
                        navigate(buildProductAssignmentPath(assignment))
                      }
                    >
                      <TableCell className='space-y-0.5 whitespace-normal'>
                        <p className='font-medium'>
                          {assignment.productName?.trim() ||
                            t('details.productAssignments.untitled')}
                        </p>
                        <p className='text-sm text-muted-foreground'>
                          {assignment.productModel?.trim() ||
                            t('details.productAssignments.noModel')}
                        </p>
                      </TableCell>
                      <TableCell>
                        {t(`details.productAssignments.roles.${assignment.role}`)}
                      </TableCell>
                      <TableCell
                        className='text-right'
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Button
                          variant='secondary'
                          size='sm'
                          onClick={() =>
                            navigate(buildProductAssignmentPath(assignment))
                          }
                        >
                          {t('common:actions.view')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      </AssociatedPartyDetailsShell>
      <EditAssociatedPartyModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        associatedParty={party}
      />
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{copy('details.delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('details.delete.description', { name: party.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t('common:actions.cancel')}
            </AlertDialogCancel>
            <Button
              variant='destructive'
              onClick={handleDelete}
              disabled={isDeleting}
              className='gap-2'
            >
              {isDeleting ? (
                <>
                  <Loader2 className='size-4 animate-spin' />
                  {t('details.delete.loading')}
                </>
              ) : (
                <>
                  <Trash2 className='size-4' />
                  {t('common:actions.delete')}
                </>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
